import { Pencil } from 'lucide-react';
import { useId } from 'react';
import { ruDate } from '../../lib/format';
import { Badge } from '../ui';
import { CardHead } from './CardHead';
import { CARD_TEXTS } from './texts-cards';
import type { DraftChangeCardProps } from './types';

/*
 * Карточка «Меняет черновик» в ленте (006, T089; FR-033; 005 FR-031; contracts/ui.md §2; мокап Main.dc.html): после
 * правки черновика агентом — что изменилось, «было → станет» по каждому полю, из ответа 005 (`items` строки ленты:
 * название правила, подпись поля и значения — тексты сервера). Бейдж «черновик» — нейтральный: так дизайн-система
 * показывает черновик. Правила не опубликованы — об этом строка под списком.
 *
 * «Было» зачёркнуто (`<del>`), «станет» — жирным (`<ins>`); для чтения с экрана — словами «было» и «станет».
 */

const { label, text, say } = CARD_TEXTS;

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

export function DraftChangeCard({ row }: DraftChangeCardProps) {
  const titleId = useId();
  return (
    <article
      aria-labelledby={titleId}
      className="flex flex-col gap-2 rounded-card border border-line bg-surface px-4 py-3 shadow-card"
    >
      <CardHead
        icon={Pencil}
        id={titleId}
        title={label.draftTitle}
        badge={<Badge dot>{label.draftBadge}</Badge>}
      />
      <ul className="flex flex-col gap-1">
        {row.items.map((it) => (
          <li
            key={`${it.rule}:${it.field}`}
            className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[13px] text-ink-2"
          >
            <span className="break-words">{say.draftItem(it.rule, lowerFirst(it.field))}</span>
            <span className="sr-only">{label.was}</span>
            <del className="break-words tabular-nums">{it.before}</del>
            <span aria-hidden>→</span>
            <span className="sr-only">{label.will}</span>
            <ins className="font-semibold break-words text-ink no-underline tabular-nums">{it.after}</ins>
          </li>
        ))}
      </ul>
      <p className="text-[13px] text-ink-2">
        {row.validFrom && <>{say.draftValidFrom(ruDate(row.validFrom))} </>}
        {text.draftNotPublished}
      </p>
    </article>
  );
}
